// 📁 GameLobby.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import socket from "../socket";
import { useNavigate } from "react-router-dom";

export default function GameLobby() {
  const [status, setStatus] = useState(null);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [postGameLock, setPostGameLock] = useState(false);
  const [endBanner, setEndBanner] = useState(null);

  // ── Identify socket immediately on connect ──────────────────────────────────
  useEffect(() => {
    const handleConnect = async () => {
      try {
        const me = await axios.get("/api/me", { withCredentials: true });
        const uid = me.data.user._id;
        setUserId(uid);
        socket.emit("identify", uid);
      } catch {}
    };

    socket.on("connect", handleConnect);
    if (socket.connected) handleConnect();
    return () => socket.off("connect", handleConnect);
  }, []);

  // ── Fetch approval/race status ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const raw = localStorage.getItem("lastGameResult");
        let locked = false;

        if (raw) {
          try {
            const last = JSON.parse(raw);
            locked = Date.now() - last.ts < 12_000;
          } catch {}
        }

        if (!cancelled) setPostGameLock(locked);

        const res = await axios.get("/api/my-approval-status", { withCredentials: true });
        const data = res.data;
        if (cancelled) return;
        setStatus(data);

        if (data.approved && data.gameId) {
          socket.emit("joinGameRoom", data.gameId);
        }

        if (!locked && data.approved && data.gameStarted) {
          navigate("/map");
        }

        if (locked) {
          setTimeout(() => { if (!cancelled) init(); }, 1500);
        }
      } catch {}
    };

    init();
    return () => { cancelled = true; };
  }, [navigate]);

  // ── Post-game result banner ─────────────────────────────────────────────────
  useEffect(() => {
    const raw = localStorage.getItem("lastGameResult");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (Date.now() - data.ts < 12_000) {
        setEndBanner(data);
        setTimeout(() => setEndBanner(null), 8000);
      }
    } catch {}
    setTimeout(() => localStorage.removeItem("lastGameResult"), 60_000);
  }, []);

  // ── Real-time: player approved (team assigned) ─────────────────────────────
  useEffect(() => {
    const handler = (payload) => {
      setStatus(prev => ({
        ...(prev || {}),
        approved: true,
        gameId: payload.gameId,
        gameName: payload.gameName,
        teamId: payload.teamId,
        teamName: payload.teamName,
        gameStarted: false,
      }));
      socket.emit("joinGameRoom", payload.gameId);
    };
    socket.on("playerApproved", handler);
    return () => socket.off("playerApproved", handler);
  }, []);

  // ── Real-time: game started ────────────────────────────────────────────────
  useEffect(() => {
    const handler = ({ gameId }) => {
      const raw = localStorage.getItem("lastGameResult");
      if (raw) return; // post-game protection
      navigate("/map");
    };
    socket.on("gameStarted", handler);
    return () => socket.off("gameStarted", handler);
  }, [navigate]);

  // ── Real-time: race started for this team ─────────────────────────────────
  useEffect(() => {
    const handler = () => navigate("/map");
    socket.on("raceStarted", handler);
    return () => socket.off("raceStarted", handler);
  }, [navigate]);

  // ── Reconnect on wake ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const onConnect = () => {
      socket.emit("identify", userId);
      if (status?.gameId) socket.emit("joinGameRoom", status.gameId);
    };
    socket.on("connect", onConnect);
    return () => socket.off("connect", onConnect);
  }, [userId, status?.gameId]);

  // ── Polling ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    let stopped = false;

    const poll = async () => {
      try {
        const res = await axios.get("/api/my-approval-status", { withCredentials: true });
        if (stopped) return;
        const data = res.data;
        setStatus(prev => {
          const prevStr = JSON.stringify(prev);
          const nextStr = JSON.stringify(data);
          return prevStr === nextStr ? prev : data;
        });

        if (data.approved && data.gameId) {
          socket.emit("joinGameRoom", data.gameId);
        }

        if (!postGameLock && data.approved && data.gameStarted) {
          navigate("/map");
        }
      } catch {}
    };

    poll();
    const t = setInterval(poll, 3500);
    return () => { stopped = true; clearInterval(t); };
  }, [userId, postGameLock, navigate]);

  // ── UI ─────────────────────────────────────────────────────────────────────
  if (!status) return <div style={{ padding: "20px" }}>Checking status…</div>;

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "bold" }}>Race Lobby</h2>

      {/* Post-game result banner */}
      {endBanner?.results && (
        <div style={{
          background: "rgba(0,0,0,0.85)", color: "white",
          padding: "12px 16px", borderRadius: "10px",
          marginTop: "12px", border: "2px solid gold"
        }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>Race Results</div>
          {endBanner.results.map((r, i) => (
            <div key={i} style={{ fontSize: "14px" }}>
              #{r.rank || i + 1} {r.teamName}
              {r.elapsedMs != null ? ` — ${Math.floor(r.elapsedMs / 60000)}m ${Math.floor((r.elapsedMs % 60000) / 1000)}s` : " — DNF"}
            </div>
          ))}
        </div>
      )}

      {/* Status messages */}
      {!status.approved && (
        <p style={{ marginTop: "12px", color: "#6b7280" }}>
          Waiting to be assigned to a team…
        </p>
      )}

      {status.approved && status.teamName && (
        <div style={{
          marginTop: "12px", padding: "12px",
          background: "#f0fdf4", border: "2px solid #16a34a",
          borderRadius: "8px"
        }}>
          <div style={{ fontWeight: "bold", color: "#15803d" }}>
            Team: {status.teamName}
          </div>
          <div style={{ fontSize: "14px", color: "#374151", marginTop: "4px" }}>
            Game: {status.gameName}
          </div>
          {status.totalCheckpoints > 0 && (
            <div style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}>
              {status.totalCheckpoints} checkpoints to collect
            </div>
          )}
        </div>
      )}

      {status.approved && !status.gameStarted && (
        <p style={{ marginTop: "10px", color: "#1d4ed8" }}>
          Waiting for race to start…
        </p>
      )}

      {status.gameStarted && !status.raceStarted && (
        <p style={{ marginTop: "10px", color: "#22c55e", fontWeight: "bold" }}>
          Race is live — drive to the START checkpoint to begin your timer!
        </p>
      )}

      {status.raceStarted && (
        <p style={{ marginTop: "10px", color: "#16a34a", fontWeight: "bold" }}>
          Your race is running! Entering map…
        </p>
      )}

      {postGameLock && (
        <p style={{ marginTop: "10px", color: "#f59e0b", fontWeight: "bold" }}>
          Race ended — standing by for next game.
        </p>
      )}
    </div>
  );
}
